import Firebase, { FirebaseErrorSources, FirebaseStackFrameLocation, FirebaseThirdPartyPattern } from '@constants/firebase';
import { getBrowserInfo } from '@ciceksepeti/cs-user-agent';
import { getFragmentName } from '@helpers/module-loader';
import { reportError } from '@helpers/firebase-client';
import { buildFingerprint } from '@helpers/fingerprint';
import { getGtmIpInfo } from '@helpers/gtm';
import { getBrowserBrandFromUserAgentData } from '@helpers/user-agent';
var seenFingerprints = new Map();
export var shouldIgnoreWindowError = function (event) {
    var _a;
    var filename = (_a = event.filename) !== null && _a !== void 0 ? _a : '';
    if (FirebaseThirdPartyPattern.test(filename))
        return true;
    if (!filename && /^script error\.?$/i.test(event.message.trim()))
        return true;
    return false;
};
var truncateData = function (_a) {
    var value = _a.value, pattern = _a.pattern, _b = _a.replacement, replacement = _b === void 0 ? '' : _b;
    var normalized = pattern ? value.replace(pattern, replacement) : value;
    return normalized.slice(0, Firebase.PARAM_MAX_LENGTH);
};
var formatErrorLine = function (_a) {
    var filename = _a.filename, line = _a.line, column = _a.column, stack = _a.stack;
    var fileName = function (value) {
        var _a;
        var pathWithoutQuery = value.trim().split('?')[0];
        return (_a = pathWithoutQuery.split('/').filter(Boolean).pop()) !== null && _a !== void 0 ? _a : '';
    };
    var file = filename ? fileName(filename) : '';
    var lineNumber = typeof line === 'number' && line > 0 ? line : undefined;
    var columnNumber = typeof column === 'number' && column > 0 ? column : undefined;
    var isLocationIncomplete = !file || lineNumber == null || columnNumber == null;
    if (isLocationIncomplete && stack) {
        for (var _i = 0, _b = stack.split('\n'); _i < _b.length; _i++) {
            var frame = _b[_i];
            var match = frame.match(FirebaseStackFrameLocation);
            var frameFile = match === null || match === void 0 ? void 0 : match[1];
            var frameLine = Number(match === null || match === void 0 ? void 0 : match[2]);
            var frameColumn = Number(match === null || match === void 0 ? void 0 : match[3]);
            if (!frameFile || frameLine <= 0)
                continue;
            if (!file)
                file = fileName(frameFile);
            if (lineNumber == null)
                lineNumber = frameLine;
            if (columnNumber == null && frameColumn > 0)
                columnNumber = frameColumn;
            break;
        }
    }
    return [file, lineNumber, columnNumber]
        .filter(function (part) { return part != null && part !== ''; })
        .join(':')
        .slice(-Firebase.PARAM_MAX_LENGTH);
};
var shouldReport = function (_a) {
    var fingerprint = _a.fingerprint;
    var now = Date.now();
    var lastSeen = seenFingerprints.get(fingerprint);
    if (lastSeen && now - lastSeen < Firebase.DEDUP_WINDOW_MS) {
        return false;
    }
    seenFingerprints.set(fingerprint, now);
    return true;
};
var buildErrorPayload = function (_a) {
    var source = _a.source, message = _a.message, errorType = _a.errorType, filename = _a.filename, line = _a.line, column = _a.column, stack = _a.stack;
    var sanitizedMessage = truncateData({
        value: message,
        pattern: /\d{4,}/g,
        replacement: 'N'
    });
    var fingerprint = buildFingerprint({ message: sanitizedMessage, errorType: errorType });
    if (!shouldReport({ fingerprint: fingerprint }))
        return null;
    var isFatalError = source === FirebaseErrorSources.ERROR_BOUNDARY ? Firebase.FATAL_TRUE : Firebase.FATAL_FALSE;
    var userAgent = navigator.userAgent;
    var clientHints = navigator.userAgentData;
    var browserInfo = getBrowserInfo(userAgent);
    var browserFromHints = getBrowserBrandFromUserAgentData({ brands: clientHints === null || clientHints === void 0 ? void 0 : clientHints.brands });
    return {
        source: source,
        message: sanitizedMessage,
        error_type: errorType,
        page_path: truncateData({ value: window.location.pathname }),
        page_url: truncateData({ value: "".concat(window.location.origin).concat(window.location.pathname) }),
        fingerprint: fingerprint,
        isFatalError: isFatalError,
        error_line: formatErrorLine({ filename: filename, line: line, column: column, stack: stack }),
        ip_address: getGtmIpInfo(),
        fragment_name: getFragmentName(),
        platform: Firebase.PLATFORM,
        browser: browserFromHints || browserInfo.name,
        browser_ver: browserInfo.version,
        os: (clientHints === null || clientHints === void 0 ? void 0 : clientHints.platform) || browserInfo.platform,
        device_type: browserInfo.mobile ? 'mobile' : 'desktop',
        resolution: "".concat(window.screen.width, "x").concat(window.screen.height),
        locale: navigator.language,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        user_agent: truncateData({ value: userAgent })
    };
};
export var reportUiError = function (_a) {
    var source = _a.source, message = _a.message, errorType = _a.errorType, filename = _a.filename, line = _a.line, column = _a.column, stack = _a.stack;
    var payload = buildErrorPayload({
        source: source,
        message: message,
        errorType: errorType,
        filename: filename,
        line: line,
        column: column,
        stack: stack
    });
    if (!payload)
        return;
    if (process.env.NODE_ENV === 'development') {
        console.info('[ui_error]', payload);
    }
    reportError(payload);
};
